/**
 * API Client - Core HTTP client with interceptors and error handling
 */
import { API_CONFIG, TOKEN_STORAGE, HTTP_STATUS, AUTH_ENDPOINTS } from "./constants";
import { transformApiError, AuthenticationError, ApiError } from "./errorHandler";
import { translate } from "../../hooks";

/**
 * Authentication endpoints that should *not* trigger the global “session expired” redirect when a 401 occurs.
 * These routes intentionally return 401/invalid-credentials during login/2FA,
 */
const AUTH_FAILURE_BYPASS = new Set([
  AUTH_ENDPOINTS.LOGIN,
  AUTH_ENDPOINTS.FORGOT_PASSWORD,
  AUTH_ENDPOINTS.RESET_PASSWORD,
  AUTH_ENDPOINTS.VERIFY_2FA,
  AUTH_ENDPOINTS.RESEND_2FA,
]);

const getAuthFailureMessage = (url) => {
  switch (url) {
    case AUTH_ENDPOINTS.LOGIN:
      return translate("AUTHENTICATION.INCORRECT_USERNAME_OR_PASSWORD");
    case AUTH_ENDPOINTS.RESET_PASSWORD:
    case AUTH_ENDPOINTS.VERIFY_2FA:
      return translate("AUTHENTICATION.INVALID_CODE");
    default:
      return translate("AUTHENTICATION.ERROR_OCCURRED");
  }
};

/**
 * Core API fetch function with centralized error handling
 * @param {string} url - API endpoint URL (relative to base)
 * @param {Object} options - Fetch options
 * @param {string} [options.method="GET"] - HTTP method
 * @param {Object} [options.body] - Request body (will be JSON stringified)
 * @param {string} [options.token] - JWT token for Authorization header
 * @returns {Promise<Object>} Response data
 * @throws {ApiError|AuthenticationError|NetworkError}
 */
export async function apiFetch(url, options = {}) {
  const { method = "GET", body = null, token = null, signal } = options;

  const headers = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}${url}`, {
      method,
      headers,
      body: body ? JSON.stringify(body) : null,
      signal,
    });

    const isUnauthorized = response.status === HTTP_STATUS.UNAUTHORIZED;
    const shouldBypassAuthRedirect = isUnauthorized && AUTH_FAILURE_BYPASS.has(url);

    // Handle authentication errors (except login/2FA endpoints where we must bubble up the 401)
    if (isUnauthorized && !shouldBypassAuthRedirect) {
      localStorage.removeItem(TOKEN_STORAGE.KEY);
      window.location.href = "/login";
      throw new AuthenticationError(translate("ERRORS.SESSION_EXPIRED"), response.status, null);
    }

    // Handle 204 No Content - no response body to parse
    if (response.status === HTTP_STATUS.NO_CONTENT) {
      if (!response.ok) {
        throw transformApiError(new Error(translate("ERRORS.API_ERROR")), {
          status: response.status,
          data: null,
        });
      }
      return { success: true };
    }

    const data = await response.json();

    if (!response.ok) {
      if (shouldBypassAuthRedirect && isUnauthorized) {
        throw new ApiError(data?.error?.message || getAuthFailureMessage(url), response.status, data);
      }

      throw transformApiError(new Error(data?.error?.message || translate("ERRORS.API_ERROR")), {
        status: response.status,
        data,
      });
    }

    return data;
  } catch (error) {
    // If already a typed error, rethrow it
    if (error.isApiError || error.isAuthenticationError || error.isNetworkError) {
      throw error;
    }

    // Transform unknown errors
    throw transformApiError(error);
  }
}

/**
 * Convenience method for GET requests
 * @param {string} path - API endpoint path
 * @param {string} [token] - JWT token
 * @returns {Promise<Object>}
 */
export async function get(path, token = null) {
  return apiFetch(path, { method: "GET", token });
}

/**
 * Convenience method for POST requests
 * @param {string} path - API endpoint path
 * @param {Object} body - Request body
 * @param {string} [token] - JWT token
 * @returns {Promise<Object>}
 */
export async function post(path, body = null, token = null, options = {}) {
  return apiFetch(path, { method: "POST", body, token, ...options });
}

/**
 * Convenience method for PUT requests
 * @param {string} path - API endpoint path
 * @param {Object} body - Request body
 * @param {string} [token] - JWT token
 * @returns {Promise<Object>}
 */
export async function put(path, body = null, token = null) {
  return apiFetch(path, { method: "PUT", body, token });
}

/**
 * Convenience method for PATCH requests
 * @param {string} path - API endpoint path
 * @param {Object} body - Request body
 * @param {string} [token] - JWT token
 * @returns {Promise<Object>}
 */
export async function patch(path, body = null, token = null) {
  return apiFetch(path, { method: "PATCH", body, token });
}

/**
 * Convenience method for DELETE requests
 * @param {string} path - API endpoint path
 * @param {Object} [body] - Optional request body
 * @param {string} [token] - JWT token
 * @returns {Promise<Object>}
 */
export async function del(path, body = null, token = null) {
  return apiFetch(path, { method: "DELETE", body, token });
}
